import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import { connectStripeExpress, getStripeStatus } from "../../api/payments";
import "../AdventureShared.css";
import "./PayoutSettings.css";

export default function PayoutSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    onboardingCompleted: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    connectedAccountId: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchStripeStatus();
    checkUrlParams();
  }, []);

  const fetchStripeStatus = async () => {
    setIsLoading(true);
    try {
      const response = await getStripeStatus();
      if (response && response.success && response.data) {
        setStripeStatus({
          connected: response.data.connected,
          onboardingCompleted: response.data.onboardingCompleted,
          chargesEnabled: response.data.chargesEnabled,
          payoutsEnabled: response.data.payoutsEnabled,
          connectedAccountId: response.data.connectedAccountId || "",
        });
      } else {
        toast.error(response?.message || "Failed to load Stripe status.");
      }
    } catch (err) {
      console.error("Failed to load Stripe status", err);
      toast.error("Error communicating with servers. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const checkUrlParams = () => {
    const statusParam = searchParams.get("status");
    if (statusParam === "success") {
      toast.success("🎉 Stripe Connected Account status updated successfully!");
      // Clean up search query
      searchParams.delete("status");
      setSearchParams(searchParams);
    }
  };

  const handleConnectStripe = async () => {
    setIsSubmitting(true);
    try {
      const returnUrl = `${window.location.origin}/facilitate/settings/payouts?status=success`;
      const refreshUrl = `${window.location.origin}/facilitate/settings/payouts`;

      const response = await connectStripeExpress(returnUrl, refreshUrl);
      if (response && response.success && response.data) {
        // Redirect browser to Stripe Connect onboarding
        window.location.href = response.data;
      } else {
        toast.error(response?.message || "Failed to initiate Stripe onboarding.");
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Failed to connect Stripe", err);
      toast.error("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const getStatusDetails = () => {
    if (!stripeStatus.connected) {
      return {
        class: "disconnected",
        title: "Payout Account Disconnected",
        desc: "Set up your payout account to start receiving payments for your bookings.",
        icon: <ErrorOutlineIcon fontSize="large" />,
      };
    }
    if (!stripeStatus.payoutsEnabled || !stripeStatus.onboardingCompleted) {
      return {
        class: "pending",
        title: "Onboarding Incomplete",
        desc: "Your account is linked, but you must complete Stripe verification details to enable payouts.",
        icon: <HelpOutlineIcon fontSize="large" />,
      };
    }
    return {
      class: "connected",
      title: "Payouts Configured",
      desc: "Your Stripe Connected Account is active and configured to receive payments.",
      icon: <CheckCircleOutlineIcon fontSize="large" />,
    };
  };

  const status = getStatusDetails();

  return (
    <div className="PayoutSettingsPage">
      <div className="adventurePageContainer">
        <div className="headerContain">
          <Header />
        </div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>
          <main className="adv-page-main animate-fade-in">
            {/* Standard Page Header */}
            <div className="adv-page-header">
              <div>
                <h1 className="adv-page-title">
                  Payout <span className="adv-page-title-accent">Settings</span>
                </h1>
                <p className="adv-page-sub">
                  Configure and manage how you receive earnings from bookings and events.
                </p>
              </div>
            </div>

            <div className="payout-settings-container">

            {isLoading ? (
              <div className="payout-loading">
                <CircularProgress color="inherit" size={40} />
                <span>Loading your Stripe Connect account status...</span>
              </div>
            ) : (
              <div className="payout-status-card">
                <div className="payout-status-row">
                  <div className={`payout-status-icon ${status.class}`}>
                    {status.icon}
                  </div>
                  <div className="payout-status-info">
                    <div className="payout-status-title">{status.title}</div>
                    <div className="payout-status-desc">{status.desc}</div>
                  </div>
                </div>

                <div className="payout-details-grid">
                  <div className={`payout-detail-item ${stripeStatus.connected ? "active" : "inactive"}`}>
                    {stripeStatus.connected ? (
                      <TaskAltIcon sx={{ color: "#28a745" }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ color: "#dc3545" }} />
                    )}
                    <div>
                      <div className="payout-detail-label">Connect Account Status</div>
                      <div className="payout-detail-value">
                        {stripeStatus.connected ? "Registered" : "Unregistered"}
                      </div>
                    </div>
                  </div>

                  <div className={`payout-detail-item ${stripeStatus.onboardingCompleted ? "active" : "inactive"}`}>
                    {stripeStatus.onboardingCompleted ? (
                      <TaskAltIcon sx={{ color: "#28a745" }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ color: "#dc3545" }} />
                    )}
                    <div>
                      <div className="payout-detail-label">Stripe KYC Verification</div>
                      <div className="payout-detail-value">
                        {stripeStatus.onboardingCompleted ? "Submitted" : "Pending Details"}
                      </div>
                    </div>
                  </div>

                  <div className={`payout-detail-item ${stripeStatus.chargesEnabled ? "active" : "inactive"}`}>
                    {stripeStatus.chargesEnabled ? (
                      <TaskAltIcon sx={{ color: "#28a745" }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ color: "#dc3545" }} />
                    )}
                    <div>
                      <div className="payout-detail-label">Charges Enabled</div>
                      <div className="payout-detail-value">
                        {stripeStatus.chargesEnabled ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>

                  <div className={`payout-detail-item ${stripeStatus.payoutsEnabled ? "active" : "inactive"}`}>
                    {stripeStatus.payoutsEnabled ? (
                      <TaskAltIcon sx={{ color: "#28a745" }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ color: "#dc3545" }} />
                    )}
                    <div>
                      <div className="payout-detail-label">Payouts Enabled</div>
                      <div className="payout-detail-value">
                        {stripeStatus.payoutsEnabled ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                {stripeStatus.connectedAccountId && (
                  <div className="payout-note-box">
                    <strong>Connected Account Reference:</strong> {stripeStatus.connectedAccountId}
                    <br />
                    Identity verification (KYC), bank account linkages, and tax settings are securely handled directly on Stripe Connect hosted portals.
                  </div>
                )}

                <div className="payout-actions">
                  {(!stripeStatus.connected || !stripeStatus.payoutsEnabled || !stripeStatus.onboardingCompleted) ? (
                    <button
                      className="payout-btn-primary"
                      onClick={handleConnectStripe}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress color="inherit" size={16} />
                          <span>Initiating...</span>
                        </>
                      ) : (
                        <>
                          <AccountBalanceIcon />
                          <span>{stripeStatus.connected ? "Complete Stripe Verification" : "Link Stripe Account"}</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      className="payout-btn-secondary"
                      onClick={handleConnectStripe}
                      disabled={isSubmitting}
                    >
                      Manage Stripe Account
                    </button>
                  )}
                  
                  <button
                    className="payout-btn-secondary"
                    onClick={fetchStripeStatus}
                    disabled={isSubmitting}
                  >
                    Refresh Status
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  </div>
  );
}
